import { useState, useEffect, useRef } from "react";
import { 
  Bold, Italic, List, Link2, Type, Plus, X, ChevronDown, ChevronUp,
  Eye, Code, Undo2, User, Building, Calendar, Link, Mail,
  GripVertical, Trash2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import { Separator } from "../ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../ui/collapsible";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";

// Simple rich text editor with basic formatting
function RichTextEditor({ value, onChange, placeholder, variables = [] }) {
  const editorRef = useRef(null);
  
  const applyFormat = (format) => {
    document.execCommand(format, false, null);
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };
  
  const insertVariable = (varName) => {
    document.execCommand('insertText', false, `{{${varName}}}`);
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };
  
  return (
    <div className="border rounded-md">
      <div className="flex items-center gap-1 p-2 border-b bg-secondary/30">
        <Button 
          type="button"
          variant="ghost" 
          size="sm" 
          onClick={() => applyFormat('bold')}
          className="h-7 w-7 p-0"
          title="Bold"
        >
          <Bold className="w-3.5 h-3.5" />
        </Button>
        <Button 
          type="button"
          variant="ghost" 
          size="sm" 
          onClick={() => applyFormat('italic')}
          className="h-7 w-7 p-0"
          title="Italic"
        >
          <Italic className="w-3.5 h-3.5" />
        </Button>
        <Button 
          type="button"
          variant="ghost" 
          size="sm" 
          onClick={() => applyFormat('insertUnorderedList')}
          className="h-7 w-7 p-0"
          title="Bullet List"
        >
          <List className="w-3.5 h-3.5" />
        </Button>
        
        <Separator orientation="vertical" className="h-5 mx-1" />
        
        {/* Variable insertion */}
        {variables.length > 0 && (
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs">
                <Type className="w-3 h-3 mr-1" />
                Insert Variable
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-48 p-2">
              <div className="space-y-1">
                {variables.map(v => (
                  <Button
                    key={v.key}
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start text-xs h-7"
                    onClick={() => insertVariable(v.key)}
                  >
                    {v.icon && <v.icon className="w-3 h-3 mr-2" />}
                    {v.label}
                  </Button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>
      
      <div
        ref={editorRef}
        contentEditable
        className="min-h-[80px] p-3 focus:outline-none text-sm"
        onInput={(e) => onChange(e.target.innerHTML)}
        dangerouslySetInnerHTML={{ __html: value || '' }}
        data-placeholder={placeholder}
        style={{ minHeight: '80px' }}
      />
    </div>
  );
}

// Checklist item manager
function ChecklistManager({ items, onChange }) {
  const addItem = () => {
    onChange([...items, ""]);
  };
  
  const updateItem = (index, value) => {
    const newItems = [...items];
    newItems[index] = value;
    onChange(newItems);
  };
  
  const removeItem = (index) => {
    onChange(items.filter((_, i) => i !== index));
  };
  
  return (
    <div className="space-y-2">
      {items.map((item, index) => (
        <div key={index} className="flex items-center gap-2">
          <span className="text-muted-foreground">•</span>
          <Input
            value={item}
            onChange={(e) => updateItem(index, e.target.value)}
            placeholder="Checklist item..."
            className="flex-1 h-8 text-sm"
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => removeItem(index)}
            className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
          >
            <X className="w-3.5 h-3.5" />
          </Button>
        </div>
      ))}
      <Button
        variant="outline"
        size="sm"
        onClick={addItem}
        className="h-8 text-xs"
      >
        <Plus className="w-3 h-3 mr-1" />
        Add Item
      </Button>
    </div>
  );
}

// Main Structured Email Editor
export default function StructuredEmailEditor({ 
  template, 
  onChange, 
  kickoffLink,
  variables = {},
  onPreview 
}) {
  const [activeMode, setActiveMode] = useState("structured"); // "structured" or "advanced"
  const [expandedSections, setExpandedSections] = useState({
    greeting: true,
    intro: true,
    cta: true,
    checklist: true,
    body: true,
    signature: true
  });
  
  // Variable options for rich text editor
  const variableOptions = [
    { key: "client_name", label: "Client Name", icon: User },
    { key: "project_name", label: "Project Name", icon: Building },
    { key: "start_date", label: "Start Date", icon: Calendar },
    { key: "estimated_delivery_date", label: "Delivery Date", icon: Calendar },
    { key: "pm_name", label: "PM Name", icon: User }
  ];
  
  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };
  
  const updateTemplate = (field, value) => {
    onChange({ ...template, [field]: value });
  };
  
  // Replace variables for preview
  const replaceVars = (text) => {
    if (!text) return text;
    let result = text;
    Object.entries(variables).forEach(([key, value]) => {
      result = result.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value || '');
    });
    return result;
  };
  
  // Generate preview HTML
  const getPreviewHtml = () => {
    const buttonUrl = template.cta_url_mode === "override" && template.cta_override_url 
      ? template.cta_override_url 
      : kickoffLink || "#";
    
    const greeting = replaceVars(template.greeting || "Hi,");
    const intro = replaceVars(template.intro || "");
    const checklistItems = (template.checklist_items || []).map(item => replaceVars(item));
    const sig = template.signature || {};
    
    return `
      <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 600px; line-height: 1.6; color: #302E2B;">
        <h2 style="margin: 0 0 20px 0; font-size: 20px; font-weight: 600;">${greeting}</h2>
        
        ${intro ? `<p style="margin: 0 0 20px 0;">${intro}</p>` : ''}
        
        <table cellpadding="0" cellspacing="0" border="0" style="margin: 24px 0;">
          <tr>
            <td align="center" bgcolor="#3D6355" style="border-radius: 6px;">
              <a href="${buttonUrl}" style="display: inline-block; padding: 14px 28px; font-size: 16px; font-weight: 600; color: #ffffff; text-decoration: none;">${template.cta_label || 'Book Your Production Planning Call'}</a>
            </td>
          </tr>
        </table>
        
        ${checklistItems.length > 0 ? `
          <div style="margin: 20px 0;">
            <p style="font-weight: 600; margin-bottom: 10px;">What to Prepare:</p>
            <ul style="margin: 0; padding-left: 20px;">
              ${checklistItems.map(item => `<li style="margin: 4px 0;">${item}</li>`).join('')}
            </ul>
          </div>
        ` : ''}
        
        ${(template.body_sections || []).map(section => {
          if (section.type === 'paragraph') {
            return `<p style="margin: 16px 0;">${replaceVars(section.content || '')}</p>`;
          }
          return '';
        }).join('')}
        
        <div style="margin-top: 30px;">
          <p style="margin: 0;">Best regards,</p>
          <p style="font-weight: 600; margin: 4px 0;">${sig.name || 'The Render Pros Team'}</p>
          ${sig.title ? `<p style="color: #565350; margin: 0;">${sig.title}</p>` : ''}
          <p style="color: #565350; margin: 0;">${sig.company || 'The Render Pros'}</p>
        </div>
      </div>
    `;
  };

  return (
    <div className="space-y-4">
      {/* Mode Toggle */}
      <div className="flex items-center justify-between">
        <Tabs value={activeMode} onValueChange={setActiveMode}>
          <TabsList className="h-8">
            <TabsTrigger value="structured" className="text-xs h-7 px-3">
              <Type className="w-3 h-3 mr-1" />
              Structured Editor
            </TabsTrigger>
            <TabsTrigger value="advanced" className="text-xs h-7 px-3">
              <Code className="w-3 h-3 mr-1" />
              Advanced (HTML)
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Editor Panel */}
        <div className="space-y-3">
          {activeMode === "structured" ? (
            <>
              {/* Subject */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Subject Line</Label>
                <Input
                  value={template.subject || ""}
                  onChange={(e) => updateTemplate("subject", e.target.value)}
                  placeholder="Email subject..."
                  className="h-9"
                />
              </div>
              
              {/* Greeting */}
              <Collapsible open={expandedSections.greeting} onOpenChange={() => toggleSection('greeting')}>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" className="w-full justify-between h-8 px-2">
                    <span className="text-xs font-medium">Greeting</span>
                    {expandedSections.greeting ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-2">
                  <Input
                    value={template.greeting || ""}
                    onChange={(e) => updateTemplate("greeting", e.target.value)}
                    placeholder="Hi {{client_name}},"
                    className="h-9 text-sm"
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">Use {"{{client_name}}"} for personalization</p>
                </CollapsibleContent>
              </Collapsible>
              
              {/* Intro */}
              <Collapsible open={expandedSections.intro} onOpenChange={() => toggleSection('intro')}>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" className="w-full justify-between h-8 px-2">
                    <span className="text-xs font-medium">Introduction</span>
                    {expandedSections.intro ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-2">
                  <RichTextEditor
                    value={template.intro || ""}
                    onChange={(v) => updateTemplate("intro", v)}
                    placeholder="Thank you for choosing us..."
                    variables={variableOptions}
                  />
                </CollapsibleContent>
              </Collapsible>
              
              {/* CTA Button */}
              <Collapsible open={expandedSections.cta} onOpenChange={() => toggleSection('cta')}>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" className="w-full justify-between h-8 px-2">
                    <span className="text-xs font-medium">Call-to-Action Button</span>
                    {expandedSections.cta ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-2 space-y-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Button Label</Label>
                    <Input
                      value={template.cta_label || ""}
                      onChange={(e) => updateTemplate("cta_label", e.target.value)}
                      placeholder="Book Your Production Planning Call"
                      className="h-9 text-sm"
                    />
                  </div>
                  
                  <div className="p-3 bg-blue-50 dark:bg-blue-950/30 rounded-md space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Link2 className="w-4 h-4 text-blue-600" />
                        <span className="text-xs font-medium">Button URL</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Label className="text-xs text-muted-foreground">Override</Label>
                        <Switch
                          checked={template.cta_url_mode === "override"}
                          onCheckedChange={(v) => updateTemplate("cta_url_mode", v ? "override" : "dynamic")}
                        />
                      </div>
                    </div>
                    
                    {template.cta_url_mode === "override" ? (
                      <div className="space-y-1.5">
                        <Input
                          value={template.cta_override_url || ""}
                          onChange={(e) => updateTemplate("cta_override_url", e.target.value)}
                          placeholder="https://..."
                          className="h-8 text-xs"
                        />
                        <p className="text-[10px] text-orange-600">⚠️ Overriding default planning call link</p>
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        URL sourced from Planning Call Link: <span className="font-medium">{kickoffLink || "(not set)"}</span>
                      </p>
                    )}
                  </div>
                </CollapsibleContent>
              </Collapsible>
              
              {/* Checklist */}
              <Collapsible open={expandedSections.checklist} onOpenChange={() => toggleSection('checklist')}>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" className="w-full justify-between h-8 px-2">
                    <span className="text-xs font-medium">Preparation Checklist</span>
                    {expandedSections.checklist ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-2">
                  <ChecklistManager
                    items={template.checklist_items || []}
                    onChange={(items) => updateTemplate("checklist_items", items)}
                  />
                </CollapsibleContent>
              </Collapsible>
              
              {/* Additional Body Sections */}
              <Collapsible open={expandedSections.body} onOpenChange={() => toggleSection('body')}>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" className="w-full justify-between h-8 px-2">
                    <span className="text-xs font-medium">Additional Content</span>
                    {expandedSections.body ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-2 space-y-2">
                  {(template.body_sections || []).map((section, index) => (
                    <div key={index} className="relative">
                      <RichTextEditor
                        value={section.content || ""}
                        onChange={(v) => {
                          const newSections = [...(template.body_sections || [])];
                          newSections[index] = { ...section, content: v };
                          updateTemplate("body_sections", newSections);
                        }}
                        placeholder="Additional content..."
                        variables={variableOptions}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        className="absolute top-1 right-1 h-6 w-6 p-0"
                        onClick={() => {
                          const newSections = template.body_sections.filter((_, i) => i !== index);
                          updateTemplate("body_sections", newSections);
                        }}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const newSections = [...(template.body_sections || []), { type: "paragraph", content: "" }];
                      updateTemplate("body_sections", newSections);
                    }}
                    className="h-8 text-xs"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add Section
                  </Button>
                </CollapsibleContent>
              </Collapsible>
              
              {/* Signature */}
              <Collapsible open={expandedSections.signature} onOpenChange={() => toggleSection('signature')}>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" className="w-full justify-between h-8 px-2">
                    <span className="text-xs font-medium">Signature</span>
                    {expandedSections.signature ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-2 space-y-2">
                  {/* The Welcome email is always signed by the Planning
                      Coordinator — the server sets it on send, so these are
                      shown, not edited (Bernard, 2026-11-03). */}
                  <p className="text-xs text-muted-foreground">
                    Welcome emails are signed by the Planning Coordinator, whoever activates the project.
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-xs">Name</Label>
                      <Input value={template.signature?.name || ""} readOnly disabled className="h-8 text-sm" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Title</Label>
                      <Input value={template.signature?.title || ""} readOnly disabled className="h-8 text-sm" />
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </>
          ) : (
            /* Advanced HTML Mode */
            <div className="space-y-3">
              <div className="p-3 bg-orange-50 dark:bg-orange-950/30 rounded-md">
                <p className="text-xs text-orange-700 dark:text-orange-300">
                  ⚠️ <strong>Advanced Mode:</strong> Editing raw HTML. Changes here will override the structured editor.
                </p>
              </div>
              <Textarea
                value={template.custom_html || getPreviewHtml()}
                onChange={(e) => updateTemplate("custom_html", e.target.value)}
                rows={20}
                className="font-mono text-xs"
                placeholder="<!DOCTYPE html>..."
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => updateTemplate("custom_html", null)}
                className="text-xs"
              >
                <Undo2 className="w-3 h-3 mr-1" />
                Reset to Structured
              </Button>
            </div>
          )}
        </div>
        
        {/* Preview Panel */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-xs font-medium flex items-center gap-1">
              <Eye className="w-3.5 h-3.5" />
              Live Preview
            </Label>
            <Badge variant="outline" className="text-[10px]">What client will see</Badge>
          </div>
          <Card className="overflow-hidden">
            <CardHeader className="py-2 px-3 bg-secondary/30 border-b">
              <p className="text-xs text-muted-foreground">Subject:</p>
              <p className="text-sm font-medium">{replaceVars(template.subject) || "No subject"}</p>
            </CardHeader>
            <CardContent className="p-4 max-h-[500px] overflow-y-auto bg-white">
              <div 
                dangerouslySetInnerHTML={{ 
                  __html: template.custom_html || getPreviewHtml() 
                }} 
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
